import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import type Link from 'ember-link/link';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

interface UiHeaderSignature {
  Args: {
    /**
     * Internal link to navigate back to. If not present, no navBack icon shown.
     */
    backLink?: Link;
    /**
     * The aria-label to set on the back link
     */
    label?: string;
    /**
     * The header subtitle. Used if no <:subtitle> block is given.
     */
    subtitle?: string;
    /**
     * The header title. Used if no <:title> block is given.
     */
    title?: string;
  };
  Blocks: {
    /**
     * If no <:title> block is given, but a default block is, render the default block
     * as the title. This exists for backwards compatibility; new code should use the
     * more-specific <:title> block instead.
     * @deprecated
     */
    default?: [];
    subtitle?: [];
    title?: [];
  };
  Element: HTMLDivElement;
}

export default class UiHeader extends Component<UiHeaderSignature> {
  get label(): string {
    return this.args.label || 'Back';
  }

  <template>
    <div class="ui__header" data-test-ui-header ...attributes>
      <div class="flex items-center">
        {{#if @backLink}}
          <a
            href={{@backLink.url}}
            class="ui__header__back-link"
            {{on "click" @backLink.transitionTo}}
            aria-label={{this.label}}
            data-test-ui-header-back-link
          >
            {{svgJar "ui-nav-back" class="ui__header__back-link--icon"}}
          </a>
        {{/if}}
        <h2 class="ui__header__title !block" data-test-ui-header-title>
          {{#if (has-block "title")}}
            {{yield to="title"}}
          {{else if (has-block)}}
            {{yield}}
          {{else}}
            {{@title}}
          {{/if}}
        </h2>
      </div>
      {{#if (has-block "subtitle")}}
        <p class="ui__header__subtitle" data-test-ui-header-subtitle>
          {{yield to="subtitle"}}
        </p>
      {{else if @subtitle}}
        <p class="ui__header__subtitle" data-test-ui-header-subtitle>
          {{@subtitle}}
        </p>
      {{/if}}
    </div>
  </template>
}
